import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getAuditLog, type AuditLogEntry } from "../api";
import PageHeader from "../components/PageHeader";
import Timestamp from "../components/Timestamp";
import { usePageTitle } from "../hooks/usePageTitle";
import { getAuditActionConfig, getAuditActionOptions } from "../config/audit";

function ActionBadge({ action }: { action: string }) {
  const { label, color } = getAuditActionConfig(action);
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}
    >
      {label}
    </span>
  );
}

export default function AuditPage() {
  usePageTitle("Audit Log");
  const navigate = useNavigate();
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<"createdAt" | "userEmail" | "action">("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  // Get action options once (memoized)
  const actionOptions = useMemo(() => getAuditActionOptions(), []);

  const PAGE_SIZE = 50;

  useEffect(() => {
    loadEntries();
  }, []);

  async function loadEntries(offset = 0, append = false) {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const data = await getAuditLog({ limit: PAGE_SIZE, offset });

      if (append) {
        setEntries((prev) => [...prev, ...data]);
      } else {
        setEntries(data);
      }

      setHasMore(data.length === PAGE_SIZE);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load audit log");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  function loadMore() {
    loadEntries(entries.length, true);
  }

  // Filter and sort entries
  const filteredEntries = entries
    .filter((entry) => {
      // Action filter
      if (actionFilter !== "all" && entry.action !== actionFilter) {
        return false;
      }
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          entry.userEmail.toLowerCase().includes(query) ||
          entry.resourceName?.toLowerCase().includes(query) ||
          entry.resourceId.toLowerCase().includes(query) ||
          entry.ipAddress?.toLowerCase().includes(query)
        );
      }
      return true;
    })
    .sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortField) {
        case "userEmail":
          aVal = a.userEmail.toLowerCase();
          bVal = b.userEmail.toLowerCase();
          break;
        case "action":
          aVal = a.action;
          bVal = b.action;
          break;
        case "createdAt":
        default:
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
      }

      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  function handleSort(field: "createdAt" | "userEmail" | "action") {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  }

  function SortIcon({ field }: { field: "createdAt" | "userEmail" | "action" }) {
    if (sortField !== field) {
      return <span className="text-gray-300 dark:text-dark-text-muted ml-1">↕</span>;
    }
    return (
      <span className="text-primary-500 dark:text-primary-400 ml-1">
        {sortDir === "asc" ? "↑" : "↓"}
      </span>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeader.Breadcrumb to="/apps" />
        <PageHeader.Title>Audit Log</PageHeader.Title>
        <PageHeader.Subtitle>Track all actions performed in the system</PageHeader.Subtitle>
      </PageHeader>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by email, resource, or IP..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-text-muted"
          />
        </div>
        <div>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="w-full sm:w-auto pl-4 pr-8 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text appearance-none bg-no-repeat bg-[length:16px_16px] bg-[right_8px_center]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
            }}
          >
            <option value="all">All Actions</option>
            {actionOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {(searchQuery || actionFilter !== "all") && (
          <button
            onClick={() => {
              setSearchQuery("");
              setActionFilter("all");
            }}
            className="text-sm text-gray-500 dark:text-dark-text-secondary hover:text-gray-700 dark:hover:text-dark-text"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Results count */}
      {(searchQuery || actionFilter !== "all") && (
        <p className="text-sm text-gray-500 dark:text-dark-text-secondary">
          Showing {filteredEntries.length} of {entries.length} entries
        </p>
      )}

      {entries.length === 0 ? (
        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-8 text-center text-gray-500 dark:text-dark-text-secondary">
          No audit log entries yet
        </div>
      ) : (
        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
            <thead className="bg-gray-50 dark:bg-dark-bg-tertiary">
              <tr>
                <th
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border whitespace-nowrap"
                  onClick={() => handleSort("createdAt")}
                >
                  When
                  <SortIcon field="createdAt" />
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border whitespace-nowrap"
                  onClick={() => handleSort("userEmail")}
                >
                  User
                  <SortIcon field="userEmail" />
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border whitespace-nowrap"
                  onClick={() => handleSort("action")}
                >
                  Action
                  <SortIcon field="action" />
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider whitespace-nowrap">
                  Resource
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider whitespace-nowrap">
                  IP Address
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-bg-secondary divide-y divide-gray-200 dark:divide-dark-border">
              {filteredEntries.map((entry) => (
                <tr
                  key={entry.id}
                  onClick={() => navigate(`/audit/${entry.id}`)}
                  className="hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary cursor-pointer"
                >
                  <td className="px-6 py-4 whitespace-nowrap align-middle text-sm text-gray-500 dark:text-dark-text-secondary">
                    <Timestamp date={entry.createdAt} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap align-middle text-sm text-gray-900 dark:text-dark-text">
                    {entry.userEmail}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap align-middle">
                    <ActionBadge action={entry.action} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap align-middle">
                    <div className="text-sm text-gray-900 dark:text-dark-text">
                      {entry.resourceName || entry.resourceId}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-dark-text-muted">
                      {entry.resourceType}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap align-middle text-sm text-gray-500 dark:text-dark-text-secondary font-mono">
                    {entry.ipAddress || "—"}
                  </td>
                </tr>
              ))}
              {filteredEntries.length === 0 && (searchQuery || actionFilter !== "all") && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-8 text-center text-gray-500 dark:text-dark-text-secondary"
                  >
                    No entries match your filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {hasMore && !searchQuery && actionFilter === "all" && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg-tertiary text-center">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium disabled:opacity-50"
              >
                {loadingMore ? "Loading..." : "Load More"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
